import type { Copy } from "../types";

export const de: Copy = {
    seo: {
      title: "Fahrzeugalter-Rechner | Alter nach Jahr und Monat prüfen",
      description:
        "Prüfen Sie das genaue Alter eines Fahrzeugs anhand des Baujahrs und eines optionalen Monats. Zeigen Sie Jahre, Monate, Tage, Gesamttage und Meilensteine in einem klaren Tool.",
      keywords: [
        "Fahrzeugalter-Rechner",
        "Autoalter",
        "Fahrzeugalter nach Jahr",
        "Baujahr Auto",
        "Monat der Fahrzeugproduktion",
        "wie alt ist mein Auto",
        "Fahrzeugalter prüfen",
        "RC Altersrechner",
        "Fahrzeugdatumsrechner",
        "Autoalter nach Monat",
        "mParivahan Alternative",
        "Parivahan Alternative",
        "RTO Fahrzeugalter-Rechner",
        "Zulassungsalter Auto prüfen",
        "Fahrzeugzulassungsalter",
      ],
      faq: [
        {
          question: "Was macht dieser Rechner?",
          answer:
            "Er berechnet das genaue Alter eines Fahrzeugs anhand des gewählten Baujahrs und des optionalen Monats und zeigt Jahre, Monate, Tage sowie Meilensteine an.",
        },
        {
          question: "Warum gibt es einen optionalen Monat?",
          answer:
            "Der Monat macht die Altersberechnung genauer. Wenn Sie keinen Monat wählen, verwendet die App den Januar des gewählten Jahres.",
        },
        {
          question: "Kann ich ein zukünftiges Jahr oder einen zukünftigen Monat wählen?",
          answer:
            "Nein. Zukünftige Jahre sind blockiert, und zukünftige Monate werden deaktiviert, wenn das gewählte Jahr das laufende Jahr ist.",
        },
      ],
    },
    headerTagline: "Fahrzeug-Tool",
    headerTitle: "Fahrzeugalter-Rechner",
    reset: "Zurücksetzen",
    switchToLight: "Zum hellen Modus wechseln",
    switchToDark: "Zum dunklen Modus wechseln",
    languageLabel: "Sprache",
    languageEnglish: "Englisch",
    languageHindi: "Hindi",
    heroEyebrow: "Fahrzeugalter, klar",
    heroTitle: "Ein klares Datumstool für das Fahrzeugalter.",
    heroDescription:
      "Wählen Sie das Baujahr. Fügen Sie nur dann einen Monat hinzu, wenn Sie ein genaueres Ergebnis möchten.",
    yearSelectLabel: "Baujahr",
    monthSelectLabel: "Baumonat",
    includeMonthLabel: "Bestimmten Monat für mehr Genauigkeit einbeziehen",
    includeMonthNote:
      "Wenn Sie keinen Monat angeben, wird für die Berechnung Januar verwendet.",
    yearOnlyBadge: "Nur Jahr",
    yearMonthBadge: "Jahr + Monat",
    yearPlaceholder: "Jahr wählen",
    monthPlaceholder: "Monat wählen",
    inputTitle: "Baujahr",
    inputLabel: "Baujahr",
    blockedDateNote: "Zukünftige Jahre sind blockiert.",
    result: "Ergebnis",
    vehicleAgeLead: "Ihr Fahrzeug ist",
    asOfToday: "Stand heute",
    years: "Jahre",
    months: "Monate",
    days: "Tage",
    totalDays: (count) => `${count.toLocaleString("en-IN")} Tage insgesamt`,
    ageLabels: {
      almostNew: "Fast neu",
      modern: "Modern",
      mature: "Ausgereift",
      classic: "Klassisch",
    },
    estimatedValue: {
      high: "Hohes Wiederverkaufspotenzial",
      healthy: "Solide Werterhaltung",
      moderate: "Mäßige Wertminderung",
      collector: "Sammlerinteresse kann steigen",
    },
    maintenanceTitle: "Wartung",
    maintenance: {
      routine: "Regelmäßiger Service und OEM-Pflege",
      watch: "Bremsen, Flüssigkeiten und Reifen beobachten",
      plan: "Verschleißteile und Inspektionen einplanen",
      specialized: "Spezialisierte Wartung wird wichtiger",
    },
    registrationTitle: "Zulassung",
    registration: {
      collector: "In einigen Regionen kann Sammlerstatus gelten",
      classic: "In einigen Regionen kann eine Oldtimer-Zulassung gelten",
      standard: "Rechnen Sie mit normalen Verlängerungen und regionalen Prüfungen",
      regular: "Üblicherweise gilt die normale Zulassung",
    },
    upcomingMilestoneTitle: "Nächster Meilenstein",
    milestoneTimelineTitle: "Meilenstein-Zeitachse",
    milestoneRemaining: ({ label, days }) => `${label} in ${days.toLocaleString("de-DE")} Tagen`,
    reached: "Erreicht",
    upcoming: "Bevorstehend",
    allMilestonesReachedTitle: "Alle aufgeführten Meilensteine erreicht",
    allMilestonesReachedBody:
      "Dieses Fahrzeug liegt bereits außerhalb des in der App angezeigten Meilensteinbereichs.",
    copySummary: "Zusammenfassung kopieren",
    shareResult: "Ergebnis teilen",
    resetMessage: "Rechner zurückgesetzt",
    summaryCopied: "Zusammenfassung in die Zwischenablage kopiert",
    clipboardFailed: "Zwischenablage konnte nicht geöffnet werden",
    emptyTitle: "Wählen Sie ein Baujahr, um die vollständige Übersicht zu sehen",
    emptyDescription:
      "Nach der Auswahl eines Jahres sehen Sie das exakte Alter, die Gesamttage, Wartungshinweise, den Oldtimer-Status und die Meilensteine.",
    shareTitle: "Fahrzeugalter-Rechner",
    summary: ({ label, years, months, days, asOf }) =>
      `Mein Fahrzeug wurde im ${label} gebaut und ist zum ${asOf} ${years} Jahre, ${months} Monate und ${days} Tage alt.`,
    milestones: {
      1: { label: "Erstes Jahr", note: "Hilfreich für Garantie- und Serviceplanung." },
      3: { label: "Drei Jahre", note: "Typischer Punkt für Wiederverkauf und Wertänderung." },
      5: { label: "Fünf Jahre", note: "Gute Zeit für eine tiefere Wartungsplanung." },
      10: { label: "Zehn Jahre", note: "Ältere Komponenten brauchen mehr Aufmerksamkeit." },
      15: { label: "Fünfzehn Jahre", note: "In vielen Regionen beginnt hier Oldtimer-Behandlung." },
      25: { label: "Fünfundzwanzig Jahre", note: "Für Sammler und Importmärkte oft interessant." },
    },
  };
